const normalize = value => String(value || '').trim().toLocaleLowerCase();
const readList = value => { try { const items = JSON.parse(value || '[]'); return Array.isArray(items) ? items.map(normalize).filter(Boolean) : []; } catch { return []; } };

export function selectCandidates(candidates, current, group, excluded = new Set()) {
  const stone = normalize(current.stone), color = normalize(current.color);
  const shapes = readList(current.shapes);
  const seen = new Set(excluded);
  const price = item => item.price !== '' && item.price != null && Number.isFinite(Number(item.price)) ? Number(item.price) : NaN;
  const currentPrice = price(current);
  const priceGap = item => Number.isFinite(price(item)) && Number.isFinite(currentPrice) ? Math.abs(price(item) - currentPrice) : Infinity;
  const eligible = candidates.filter(candidate => {
    if (!candidate.id || candidate.id === current.productId || seen.has(candidate.id)) return false;
    const stones = readList(candidate.stones), colors = readList(candidate.colors);
    const eligible = group === 'stone' ? stone && stones.includes(stone) : color && colors.includes(color) && (!stone || !stones.includes(stone));
    if (eligible) seen.add(candidate.id);
    return eligible;
  }).sort((a, b) => {
    const sameSize = candidate => Boolean(current.size) && normalize(candidate.size) === normalize(current.size);
    const sameShape = candidate => readList(candidate.shapes).some(shape => shapes.includes(shape));
    return Number(sameSize(b)) - Number(sameSize(a)) || Number(sameShape(b)) - Number(sameShape(a)) || (priceGap(a) < priceGap(b) ? -1 : priceGap(a) > priceGap(b) ? 1 : 0);
  });
  if (!eligible.length) return [];
  const featureList = value => [...new Set(readList(value))].sort().join('|');
  const profiles = new Map(eligible.map(item => [item.id, [featureList(item.stones), featureList(item.shapes), normalize(item.size)]]));
  const selected = [eligible[0]];
  const distance = (a, b) => profiles.get(a.id).reduce((total, feature, index) => total + Number(Boolean(feature && profiles.get(b.id)[index] && feature !== profiles.get(b.id)[index])), 0);
  const variety = item => Math.min(...selected.map(chosen => distance(item, chosen)));
  const choose = pool => pool.filter(item => !selected.some(chosen => chosen.id === item.id)).reduce((best, item) => !best || variety(item) > variety(best) ? item : best, null);
  const cheaper = eligible.filter(item => price(item) > 0 && price(item) < currentPrice);
  const meaningfullyCheaper = cheaper.filter(item => price(item) <= currentPrice * 0.9);
  const affordablePool = meaningfullyCheaper.length ? meaningfullyCheaper : cheaper;
  if (!selected.some(chosen => affordablePool.some(item => item.id === chosen.id))) {
    const affordable = choose(affordablePool);
    if (affordable) selected.push(affordable);
  }
  while (selected.length < 4) {
    const next = choose(eligible);
    if (!next) break;
    selected.push(next);
  }
  return selected;
}

if (typeof customElements !== 'undefined' && !customElements.get('ab-pdp-discovery')) {
  customElements.define('ab-pdp-discovery', class extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      // Only carry Shopify's actual draft context; never hard-code a draft ID into release code.
      const requested = new URL(location.href).searchParams.get('preview_theme_id');
      this.previewId = window.Shopify?.theme?.role === 'unpublished' ? String(window.Shopify.theme.id) : requested;
      this.decorate(document.querySelectorAll('[data-discovery-catalog]'));
      this.lifecycleController = new AbortController();
      document.addEventListener('product:rerender', () => queueMicrotask(() => this.syncLinks()), { capture: true, signal: this.lifecycleController.signal });
      document.addEventListener('click', event => {
        const link = event.target.closest('[data-discovery-jump]');
        if (!link || link.closest('[data-discovery-links]')?.dataset.productId !== this.dataset.productId) return;
        const group = this.querySelector(`[data-discovery-group="${link.dataset.discoveryJump}"]`);
        if (!group || group.hidden) return;
        event.preventDefault();
        group.querySelector('h2').focus({ preventScroll: true });
        group.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      }, { signal: this.lifecycleController.signal });
      this.load();
    }
    disconnectedCallback() { this.controller?.abort(); this.lifecycleController?.abort(); this.reviewObserver?.disconnect(); clearTimeout(this.reviewTimeout); }
    scrollToRequestedReviews() {
      // Judge.me fills its review widget after page load. Wait for it and the discovery
      // layout so a card's review link lands at the selected product's reviews.
      if (location.hash !== '#judgeme_product_reviews') return;
      const reveal = () => {
        const target = document.getElementById('judgeme_product_reviews') || document.querySelector('.jdgm-review-widget');
        if (!target || !target.querySelector('.jm-review-widget, .jdgm-rev-widg, [class*="jm-review"]')) return false;
        this.reviewObserver?.disconnect();
        clearTimeout(this.reviewTimeout);
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        target.scrollIntoView({ behavior: 'instant', block: 'start' });
        return true;
      };
      if (reveal()) return;
      this.reviewObserver = new MutationObserver(reveal);
      this.reviewObserver.observe(document.body, { childList: true, subtree: true });
      this.reviewTimeout = setTimeout(() => this.reviewObserver.disconnect(), 12000);
    }
    syncLinks() {
      document.querySelectorAll(`[data-discovery-links][data-product-id="${this.dataset.productId}"]`).forEach(nav => {
        let visible = false;
        nav.querySelectorAll('[data-discovery-jump]').forEach(link => {
          const group = this.querySelector(`[data-discovery-group="${link.dataset.discoveryJump}"]`);
          link.hidden = !group || group.hidden;
          visible ||= !link.hidden;
        });
        nav.hidden = !visible;
      });
    }
    decorate(links) {
      links.forEach(link => {
        const url = new URL(link.getAttribute('href'), location.origin);
        if (url.origin !== location.origin) return;
        if (this.previewId) {
          url.searchParams.set('preview_theme_id', this.previewId);
        }
        link.href = url.pathname + url.search + url.hash;
      });
    }
    async fetchCandidates(group) {
      const url = new URL(group.dataset.source, location.origin);
      url.searchParams.set('section_id', 'ab-pdp-discovery-source');
      if (this.previewId) url.searchParams.set('preview_theme_id', this.previewId);
      const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Discovery response unavailable');
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (document.querySelector('[data-discovery-source]')?.dataset.filtered !== 'true') throw new Error('Missing native filter');
      return [...document.querySelectorAll('template[data-discovery-candidate]')].map(template => ({ ...template.dataset, template }));
    }
    async load() {
      const groups = [...this.querySelectorAll('[data-discovery-group]')];
      const timeout = setTimeout(() => this.controller.abort(), 12000);
      const results = await Promise.allSettled(groups.map(group => this.fetchCandidates(group)));
      clearTimeout(timeout);
      if (!this.isConnected) return;
      const used = new Set();
      let rendered = 0;
      groups.forEach((group, index) => {
        if (results[index].status !== 'fulfilled') return;
        const selected = selectCandidates(results[index].value, this.dataset, group.dataset.discoveryGroup, used);
        if (!selected.length) return;
        const grid = group.querySelector('[data-discovery-grid]');
        // Cards are the theme product card; names come from snippets/card-title.liquid.
        selected.forEach(candidate => {
          used.add(candidate.id);
          grid.append(candidate.template.content.cloneNode(true));
        });
        this.decorate(grid.querySelectorAll('a[href*="/products/"]'));
        group.hidden = false;
        rendered++;

      });
      this.querySelector('[data-discovery-fallback]').hidden = rendered > 0;
      this.dataset.state = rendered ? 'ready' : 'fallback';
      this.syncLinks();
      this.scrollToRequestedReviews();
    }
  });
}

